import {ResponsiveLayout, NavLayout} from '@layouts';
import {COURSE, UNPUBLISHED_COURSE_DETAIL} from '@constants';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {SeoIntrup} from '@layouts/SeoLayout';
import {isCSR} from '@jobseeker/lotus/utils';
import {InjectSearchOverlay} from '@components';
import dynamic from 'next/dynamic';
import {AppState, Course} from '@jobseeker/common-core/types';
import {useRouter} from 'next/router';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {Loader} from '@jobseeker/lotus/components';
import {useState} from 'react';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME || '';
const isAppIimjobs = PLATFORM === 'iimjobs';
const isAppHirist = PLATFORM === 'hirist';

const CourseDetailHarness = dynamic(
  () =>
    import('@jobseeker/client-harness/components/CourseHarness/CourseDetail'),
  {
    loading: () => <></>,
    ssr: true,
  }
);

const LoginForm = dynamic(
  () =>
    isAppIimjobs
      ? import(
          '@jobseeker/client-harness/components/AuthenticationHarness/AuthForm'
        )
      : isAppHirist
      ? import(
          '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
        )
      : Promise.reject(''),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

type CourseDetailProps = {
  coursePublishedId: number;
  courseDetail: Course | undefined;
  userId: number;
  token: string;
  forUnpublishedCourse?: boolean;
};

const CourseDetailComponent = ({
  courseDetail,
  forUnpublishedCourse = false,
}: CourseDetailProps) => {
  const [loginOpen, setLoginOpen] = useState(false);

  const {searchOverlay: isSearchEnabled, enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const onLoginSuccess = () => {
    if (isCSR()) {
      setTimeout(() => window.location.reload(), 600);
    }
  };

  const handleEnquiryInLoggedOutState = () => {
    setLoginOpen(true);
  };

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Courses',
      item: forUnpublishedCourse ? '/course_unpublished_list' : '/courses',
    },
    {
      name: courseDetail?.title ?? 'Course removed',
      item: '',
    },
  ];

  const router = useRouter();
  const dispatch = useDispatch();
  const {refinv = 'NA', reforg = 'NA'} = router.query;

  return (
    <NavLayout page={COURSE}>
      <ResponsiveLayout
        breadcrumbElements={
          !!courseDetail?.id
            ? breadcrumbElements
            : isAppHirist
            ? breadcrumbElements
            : []
        }
        variant={VersionVariants.VERSION_TWO}
      >
        <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
        {isCSR() && (
          <SeoIntrup
            title={`${courseDetail?.title ?? 'Course not found'} | ${
              isAppIimjobs ? 'IIMJobs' : 'Hirist'
            }`}
            description={`${
              courseDetail?.title ?? 'Courses'
            } for working professionals and entrepreneurs to learn & develop advanced leadership & management skills.`}
            pageNo={0}
            nextPage={false}
            type={forUnpublishedCourse ? UNPUBLISHED_COURSE_DETAIL : COURSE}
          />
        )}
        {!!courseDetail?.id && (
          <CourseDetailHarness
            refinv={refinv.toString() ?? 'NA'}
            reforg={reforg.toString() ?? 'NA'}
            {...courseDetail}
            handleEnquiryInLoggedOutState={handleEnquiryInLoggedOutState}
            forUnpublishedCourse={forUnpublishedCourse}
          />
        )}
        {!courseDetail?.id && (
          <>
            {forUnpublishedCourse
              ? 'No data found'
              : 'This course has been either closed or removed by the administrator.'}
          </>
        )}
        {(loginOpen || enableLoginPopup) &&
          (isAppIimjobs ? (
            <Dialog
              title="Login to your account"
              subtitle="Welcome back! 👋"
              invertedTitle
              variant={VersionVariants.VERSION_TWO}
              open={loginOpen || enableLoginPopup}
              onClose={() => setLoginOpen(false)}
            >
              <LoginForm
                onSuccess={onLoginSuccess}
                variant={VersionVariants.VERSION_TWO}
              />
            </Dialog>
          ) : isAppHirist ? (
            <LoginForm
              variant={VersionVariants.VERSION_TWO}
              openDialog={enableLoginPopup || loginOpen}
              setOpenDialog={() => {
                dispatch({
                  type: 'AppContext/set_login_popup',
                  payload: false,
                });
                setLoginOpen(false);
              }}
              enableSignIn={true}
              onSuccess={() => window.location.reload()}
            />
          ) : (
            <></>
          ))}
      </ResponsiveLayout>
    </NavLayout>
  );
};

export default CourseDetailComponent;
